import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Truck, Landmark, ScrollText, Wallet, Briefcase, Globe,
  ArrowUpRight, WifiOff, Activity, ShieldCheck, RefreshCw, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { ExecutiveCommandTour } from "../components/ExecutiveCommandTour";
import { useAuth } from "../context/AuthContext";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;

/* ═══════════════════════════════════════════════════════════════════
 *  Vinci Executive Command · 6-tile mission control for admins.
 *  Every tile is hardened: any fetch failure falls back to "Offline"
 *  and the tile still renders + still links to its inner page.
 * ═══════════════════════════════════════════════════════════════════ */
const TILES = [
  {
    key: "logistics",
    icon: Truck,
    title: "Logistics",
    subtitle: "Operations Map · Fleet Pulse",
    href: "/admin?tab=operations",
    accent: "#2FB999",
    metric: (t) => t.logistics,
    metricLabel: "Active orders in flight",
  },
  {
    key: "financials",
    icon: Landmark,
    title: "Financials",
    subtitle: "Zoho Books · Real-time Sync",
    href: "/admin?tab=zoho-books",
    accent: "#D8B23E",
    metric: (t) => t.financials,
    secondary: (t) => t.financials?.gmv,
    secondaryLabel: "GMV (30d)",
    secondaryFmt: (v) => inr(v),
    metricLabel: "Active wallets",
  },
  {
    key: "legal",
    icon: ScrollText,
    title: "Legal Desk",
    subtitle: "Zoho Contracts · Lifecycle",
    href: "/admin?tab=legal-desk",
    accent: "#B87333",
    metric: (t) => t.legal,
    secondary: (t) => t.legal?.expiring_30d,
    secondaryLabel: "Expiring ≤30d",
    metricLabel: "Active contracts",
  },
  {
    key: "wallet",
    icon: Wallet,
    title: "Wallet Master",
    subtitle: "Executive Induction · Ledger",
    href: "/admin?tab=wallet",
    accent: "#F6E4A6",
    metric: (t) => t.wallet,
    secondary: (t) => t.wallet?.pending_induction,
    secondaryLabel: "Induction pending",
    metricLabel: "Executive wallets",
  },
  {
    key: "careers",
    icon: Briefcase,
    title: "Careers",
    subtitle: "Zoho Recruit · Talent Pipeline",
    href: "/admin?tab=careers",
    accent: "#8A6A1A",
    metric: (t) => t.careers,
    metricLabel: "Applications (30d)",
  },
  {
    key: "live_pulse",
    icon: Globe,
    title: "Live Pulse",
    subtitle: "Visitor Geo · Session Heat",
    href: "/admin?tab=live-pulse",
    accent: "#2FB999",
    metric: (t) => t.live_pulse,
    metricLabel: "Active sessions · last 15m",
  },
];


export default function ExecutiveCommand() {
  const { api, user } = useAuth();
  const nav = useNavigate();
  const [tiles, setTiles] = useState(null);        // null → still loading
  const [globalOffline, setGlobalOffline] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!user) return;
    if ((user.role || "").toLowerCase() !== "admin") {
      toast.error("Executive Command is admin-only");
      nav("/admin", { replace: true });
    }
  }, [user, nav]);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setTiles(data?.tiles || {});
      setGlobalOffline(false);
    } catch (e) {
      // Whole endpoint dead → mark everything offline but still render the grid
      console.warn("Executive Command · /admin/stats fetch failed", e);
      setTiles({});
      setGlobalOffline(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  useEffect(() => { load(); }, [api]);           // eslint-disable-line

  const refresh = () => { setRefreshing(true); load(); };

  return (
    <div className="min-h-screen bg-[#050505] text-[#F5F5F0]" data-testid="executive-command-page">
      <Header />

      {/* HERO STRIP */}
      <section className="relative pt-32 pb-14 px-6 md:px-16 overflow-hidden">
        <div className="absolute inset-0 -z-10 pointer-events-none"
              style={{
                background: "radial-gradient(1000px 500px at 15% 0%, rgba(216,178,62,0.12), transparent 60%),"
                          + "radial-gradient(900px 500px at 85% 20%, rgba(184,115,51,0.08), transparent 60%)",
              }} />
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-start justify-between flex-wrap gap-6">
            <div>
              <div className="font-grotesk text-[10px] uppercase tracking-[0.4em] text-[#8A6A1A]"
                    data-testid="ec-overline">
                Vinci · Executive Command
              </div>
              <h1 className="mt-3 font-serif text-4xl md:text-6xl leading-tight tracking-tight"
                   data-testid="ec-title">
                Mission control for
                <span className="block bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] bg-clip-text text-transparent">
                  the entire trade desk.
                </span>
              </h1>
              <p className="mt-6 text-[#C9C9C2] text-sm md:text-base max-w-2xl leading-relaxed">
                Six windows into your platform's pulse — logistics, capital, legal,
                talent, wallets, live visitors. Every tile is a two-tap dive
                into the underlying console.
              </p>
            </div>
            <div className="flex items-center gap-3">
              {globalOffline && (
                <span className="inline-flex items-center gap-2 text-[#C0392B] font-grotesk text-[10px] uppercase tracking-[0.28em] px-3 py-1.5 border border-[#C0392B]/40"
                       data-testid="ec-offline-banner">
                  <WifiOff className="w-3.5 h-3.5" /> Stats offline · degraded
                </span>
              )}
              <button
                data-testid="ec-refresh"
                onClick={refresh}
                disabled={refreshing}
                className="inline-flex items-center gap-2 px-4 py-2 border border-white/15 hover:border-[#B87333] font-grotesk text-[10px] uppercase tracking-[0.28em] transition-colors disabled:opacity-50"
              >
                {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Refresh
              </button>
              <Link
                to="/admin"
                data-testid="ec-open-admin"
                className="inline-flex items-center gap-2 px-4 py-2 border border-[#B87333]/60 hover:bg-[#B87333] hover:text-black font-grotesk text-[10px] uppercase tracking-[0.28em] transition-colors"
              >
                Full Admin →
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="h-px bg-gradient-to-r from-transparent via-[#D8B23E]/40 to-transparent" />

      {/* GRID */}
      <section className="py-14 px-6 md:px-16">
        <div className="max-w-[1600px] mx-auto">
          {loading ? (
            <div className="py-24 flex justify-center" data-testid="ec-loading">
              <Loader2 className="w-6 h-6 text-[#B87333] animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                 data-testid="ec-grid">
              {TILES.map((tile, i) => (
                <CommandTile
                  key={tile.key}
                  tile={tile}
                  data={tiles?.[tile.key]}
                  offline={globalOffline}
                  delay={i * 0.05}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />

      {/* First-time admin overlay tour */}
      {!loading && <ExecutiveCommandTour />}
    </div>
  );
}


function CommandTile({ tile, data, offline, delay }) {
  const Icon = tile.icon;
  const nav = useNavigate();
  const metric = tile.metric ? tile.metric({ [tile.key]: data }) : null;
  const secondary = tile.secondary ? tile.secondary({ [tile.key]: data }) : null;

  const isTileOffline = offline || (!!metric && metric.offline === true);
  const shownValue = isTileOffline ? "OFFLINE" : (metric ? metric.value ?? 0 : 0);

  const isAdminHref = tile.href.startsWith("/admin");
  const go = (e) => {
    e?.preventDefault?.();
    nav(tile.href);
  };

  return (
    <motion.a
      href={tile.href}
      onClick={go}
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`ec-tile-${tile.key}`}
      className="group relative block border border-white/10 hover:border-[#D8B23E]/50 bg-black/30 backdrop-blur-md transition-all overflow-hidden focus:outline-none focus:border-[#D8B23E]"
      style={{ minHeight: 200 }}
    >
      {/* Gilded top hairline */}
      <div className="absolute inset-x-0 top-0 h-px"
           style={{ background: `linear-gradient(90deg, transparent, ${tile.accent}, transparent)` }} />

      <div className="p-6 md:p-8">
        <div className="flex items-start justify-between gap-4">
          <div className="grid place-items-center w-11 h-11 rounded-sm border"
                style={{
                  borderColor: `${tile.accent}66`,
                  background: `${tile.accent}12`,
                }}>
            <Icon className="w-5 h-5" style={{ color: tile.accent }} />
          </div>
          <div className="text-right">
            <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#8A6A1A]">
              {tile.subtitle}
            </div>
            {isTileOffline && (
              <div className="mt-1 inline-flex items-center gap-1 text-[#C0392B] font-grotesk text-[9px] uppercase tracking-[0.22em]"
                    data-testid={`ec-tile-${tile.key}-offline`}>
                <WifiOff className="w-3 h-3" /> Offline
              </div>
            )}
          </div>
        </div>

        <h3 className="mt-6 font-serif text-2xl md:text-3xl text-[#F5F5F0] group-hover:text-[#F6E4A6] transition-colors">
          {tile.title}
        </h3>
        <div className="mt-4">
          <div className={`font-serif ${isTileOffline ? "text-2xl text-[#8A8A82]" : "text-4xl"}`}
               style={{ color: isTileOffline ? undefined : tile.accent }}
               data-testid={`ec-tile-${tile.key}-value`}>
            {isTileOffline ? "—" : shownValue}
          </div>
          <div className="text-[10px] font-grotesk uppercase tracking-[0.24em] text-[#8A8A82] mt-1">
            {tile.metricLabel}
          </div>
          {secondary && !secondary.offline && tile.secondaryLabel && (
            <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
              <span className="text-[10px] font-grotesk uppercase tracking-[0.24em] text-[#8A6A1A]">
                {tile.secondaryLabel}
              </span>
              <span className="text-sm font-mono text-[#F5F5F0]"
                    data-testid={`ec-tile-${tile.key}-secondary`}>
                {tile.secondaryFmt ? tile.secondaryFmt(secondary.value) : secondary.value ?? 0}
              </span>
            </div>
          )}
          {secondary && secondary.offline && tile.secondaryLabel && (
            <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between text-[#8A8A82]">
              <span className="text-[10px] font-grotesk uppercase tracking-[0.24em]">
                {tile.secondaryLabel}
              </span>
              <span className="text-[10px] font-grotesk uppercase tracking-[0.24em]"
                    data-testid={`ec-tile-${tile.key}-secondary-offline`}>Offline</span>
            </div>
          )}
        </div>

        <div className="mt-6 inline-flex items-center gap-1.5 font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#B87333] group-hover:text-[#F6E4A6] transition-colors">
          Enter {tile.title}
          <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </div>
      </div>
    </motion.a>
  );
}
